/**
 * Credits context — single source of truth for the user's balance.
 *
 * Why a context: the AppNav pill, OutOfCreditsModal, ConfirmBriefingModal,
 * and PaymentSuccess screen all need to read AND refresh credits. A
 * per-component fetch would race (PackPicker pays → /me/credits refetch
 * in two places returns different values during the webhook race).
 *
 * Refresh contract:
 *   · `refresh()` re-fetches /me/credits from server.
 *   · Caller invokes after any operation that should change balance:
 *     - briefing start success → debit happened, refresh
 *     - PaymentSuccess mount   → webhook may have landed, refresh
 *     - 402 INSUFFICIENT_CREDITS → confirms balance, refresh
 *
 * We do NOT poll. The user explicitly triggers refresh through actions;
 * polling /me/credits across 1,000+ idle browser tabs is wasted load.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";
import { getMyCredits, ApiError } from "@/lib/api";
import { useAuth } from "@/auth";

interface CreditsState {
  credits: number | null;          // null until first load completes
  // Mirrors backend BILLING_ENFORCED. Drives the ConfirmBriefingModal
  // pre-flight gate — when false the Run-briefing button stays
  // actionable regardless of balance (the backend won't 402 anyway).
  // Defaults false until the first /me/credits returns, so the gate
  // never fires before we know the policy.
  enforced: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

const CreditsCtx = createContext<CreditsState | null>(null);

export function CreditsProvider({ children, enabled = true }: PropsWithChildren<{ enabled?: boolean }>) {
  const { user } = useAuth();
  const [credits, setCredits] = useState<number | null>(null);
  const [enforced, setEnforced] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || !enabled) {
      setCredits(null);
      return;
    }
    setLoading(true);
    try {
      const r = await getMyCredits();
      setCredits(r.credits);
      setEnforced(!!r.enforced);
    } catch (e) {
      // Auth blip or transient 5xx — don't blow away the last known value.
      // The pill stays on the previous reading until a successful refresh.
      if (!(e instanceof ApiError) || e.status !== 401) {
        console.warn("[credits] refresh failed:", e);
      }
    } finally {
      setLoading(false);
    }
  }, [user, enabled]);

  // Load on sign-in. Clear on sign-out.
  useEffect(() => {
    if (user && enabled) {
      void refresh();
    } else {
      setCredits(null);
    }
  }, [user, enabled, refresh]);

  return (
    <CreditsCtx.Provider value={{ credits, enforced, loading, refresh }}>
      {children}
    </CreditsCtx.Provider>
  );
}

export function useCredits(): CreditsState {
  const ctx = useContext(CreditsCtx);
  if (!ctx) {
    throw new Error("useCredits must be inside <CreditsProvider>");
  }
  return ctx;
}
